import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@supabase/supabase-js";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import axios from "axios";

const WEBHOOK_URL = import.meta.env.VITE_SECTOR_SUMARIZE_WEBHOOK_URL ?? "https://n8n.rvcarbon.com.br/webhook/sector-sumarize";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "https://ufmnyoicekbzqvoddkur.supabase.co";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "sb_publishable_z6k3AuVDJVlg4VfQSy14Qw_3ckCahFb";
const SECTOR_ANALYSES_TABLE = "sector_analyses";



export interface SectorSummarizeResponse {
    sector_id: string;
    last_analysis_at: string;
    soilAnalyses: { titulo: string, texto: string }[];
    leafTissueAnalysis: { titulo: string, texto: string }[];
    soilAnalysisHistory: { titulo: string, texto: string }[];
    hasSectorAnlysis: boolean;
    status?: string;
}

const getSectorSummarizeQueryKey = (sectorId: string) => ["sector-sumarize", sectorId];

const supabaseClient = SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
    : null;

type RealtimeStatus = "idle" | "connecting" | "subscribed" | "unavailable" | "error";

export function useGetSectorSumarize(sectorId: string) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => getSectorSummarizeQueryKey(sectorId), [sectorId]);
    const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>(
        supabaseClient ? "idle" : "unavailable"
    );

    useEffect(() => {
        if (!sectorId || !supabaseClient) {
            setRealtimeStatus(supabaseClient ? "idle" : "unavailable");
            return;
        }

        setRealtimeStatus("connecting");
        const channel = supabaseClient
            .channel(`sector-sumarize-${sectorId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: SECTOR_ANALYSES_TABLE,
                    filter: `sector_id=eq.${sectorId}`,
                },
                (payload: RealtimePostgresChangesPayload<SectorSummarizeResponse>) => {
                    console.log('[supabase realtime payload]', payload);
                    console.log('[supabase realtime new]', payload.new);
                    if (!payload.new) return;

                    const next = payload.new as SectorSummarizeResponse;

                    if (next.status !== 'success') return;

                    queryClient.setQueryData<SectorSummarizeResponse>(queryKey, next);
                }
            )
            .subscribe((status, err) => {
                console.info(`[supabase] Canal de resumo do setor ${sectorId}: ${status}.`);

                if (status === "SUBSCRIBED") {
                    setRealtimeStatus("subscribed");
                    return;
                }

                if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
                    console.warn(`[supabase] Falha no canal de resumo do setor ${sectorId}.`, err);
                    setRealtimeStatus("error");
                }
            });

        return () => {
            supabaseClient.removeChannel(channel);
        };
    }, [queryClient, queryKey, sectorId]);

    return useQuery({
        queryKey,

        queryFn: async (): Promise<SectorSummarizeResponse> => {
            const { data } = await axios.get<SectorSummarizeResponse>(
                WEBHOOK_URL,
                {
                    params: {
                        sectorId,
                    },
                }
            );

            return data;
        },

        enabled: !!sectorId && (!supabaseClient || realtimeStatus === "subscribed" || realtimeStatus === "error"),
    });
}
